/**
 * Lab / bet-service workstation APIs → Detail Book UI model.
 *
 * - `getTotalBooks1` / ForGroup → runners + root tree (+ optional `liveRates`)
 * - `getBetDetailsForWorkstation` / ForGroup → bet ticker rows
 * - `getMemberNetExposure` / ForGroup → headline NE
 * - `getBooksForBackend` / ForGroup → event/market metadata fallback
 * - `getBookForDownline` → family-tree expand rows
 */

import type { BookType } from '@/features/net-exposure/fixture'
import type {
  BookEventGroupBackend,
  BookHorseAmount,
  BookMarketBackend,
} from '@/api/map/books'
import type { LiveRatePayload } from '@/api/map/rates'
import type {
  WorkstationBet,
  WorkstationBetSide,
  WorkstationBook,
  WorkstationHorseAmount,
  WorkstationMarketType,
  WorkstationRunner,
  WorkstationTreeNode,
} from '@/features/net-exposure/workstation-fixture'

/** OpenAPI `TotalBooks1Request`. */
export type TotalBooks1Request = {
  user: string
  marketId: string
  loggedInMember?: string
  username?: string
  accessLevel?: string
  selectedType?: BookType | string
  page?: number
}

/** OpenAPI `TotalBooks1Response` book row. */
export type TotalBooks1BookRow = BookMarketBackend & {
  username?: string
}

export type TotalBooks1Response = {
  page: number
  total: number
  type?: 'matchodds' | 'bookmaker' | 'fancy' | 'lineLadder'
  book: TotalBooks1BookRow[]
  /**
   * Socket-shaped catalog odds from Agent bet-service (`market.rates` / BM runners).
   * Seeds Detail Book before the first SSE tick (Frontend Player-Markets parity).
   */
  liveRates?: LiveRatePayload
}

/** Lab `Book/getLadder` (+ getLadderTotal) body. */
export type GetLadderRequest = {
  sessionId: string
  userId: string
  level?: string
  selectedType?: BookType | string
  type?: string
}

export type FancyPtLadderRow = {
  startScore: number
  endScore: number | 'Infinite'
  amount: number
  amountCommission: number
}

/** Lab `Bet.createLadder` data payload. */
export type GetLadderResponse = {
  ladder: FancyPtLadderRow[]
  isBetPlaced: boolean
  matchData?: Array<{ runs: number; lt: number; gt: number }>
}

/** OpenAPI `MemberNetExposureRequest` / response. */
export type MemberNetExposureRequest = {
  _id: string
  marketId?: string
}

export type MemberNetExposureResponse = {
  netExposure: number
}

/** OpenAPI `BetDetailsForWorkstationRequest`. */
export type BetDetailsForWorkstationRequest = {
  user: string
  marketId: string
  page?: number
  maxRow?: number
  userName?: string | string[]
  currencyType?: string
  fromDate?: string
  toDate?: string
  accessLevel?: string
}

/** OpenAPI `WorkstationBetRow`. */
export type WorkstationBetRow = {
  betId: string
  userName: string
  selectionName: string
  marketName: string
  event: string
  type: string
  stake: number
  betRate: number
  /** Fancy / session line (orders.runs). Null/omitted for match odds. */
  runs?: number | null
  averagePriceMatched?: number
  amountExecuted?: number
  amountToBePlacedOnBetfair?: number
  status: string
  IP?: string
  currencyType?: string
  marketId: string
  placedDate?: string | null
  matchedTime?: string | null
  createdAt?: string | null
  player?: { rate?: number; id?: string }
}

export type BetDetailsForWorkstationResponse = {
  result: WorkstationBetRow[]
  count: number
  maxRow: number
}

export function labTypeToWorkstation(
  type: TotalBooks1Response['type'] | undefined,
  marketName?: string,
  fallback: WorkstationMarketType = 'match',
): WorkstationMarketType {
  if (type === 'fancy' || type === 'lineLadder') return 'fancy'
  if (type === 'bookmaker') return 'bookmaker'
  if (type === 'matchodds') return 'match'
  const n = (marketName || '').toLowerCase()
  if (n.includes('fancy') || n.includes('session')) return 'fancy'
  if (n.includes('bookmaker') || (marketName || '').includes('#')) return 'bookmaker'
  return fallback
}

function emptyOdds(): { back: WorkstationRunner['back']; lay: WorkstationRunner['lay'] } {
  return {
    back: [
      { price: 0, size: 0 },
      { price: 0, size: 0 },
      { price: 0, size: 0 },
    ],
    lay: [
      { price: 0, size: 0 },
      { price: 0, size: 0 },
      { price: 0, size: 0 },
    ],
  }
}

function horseAmount(h: BookHorseAmount): number {
  return Number(h.amount) || 0
}

function horseName(h: BookHorseAmount, index: number): string {
  const named = (h.name ?? '').trim()
  if (named) return named
  const id = String(h.horse || '').trim()
  return id || `Runner ${index + 1}`
}

function isDrawHorseName(name: string): boolean {
  return /^(x|the draw|draw)$/i.test(name.trim())
}

/** Lab / list grid: Draw / X always last among runners. */
export function horsesDrawLast<T extends { name?: string }>(horses: T[]): T[] {
  if (horses.length < 3) return horses
  const drawAt = horses.findIndex((h) => isDrawHorseName(h.name ?? ''))
  if (drawAt < 0 || drawAt === horses.length - 1) return horses
  const draw = horses[drawAt]
  if (!draw) return horses
  return [...horses.filter((_, i) => i !== drawAt), draw]
}

/** Build runners from Lab horse P/L. Live batb/batl overlay comes from Socket-API. Draw column last. */
export function runnersFromHorses(
  horses: BookHorseAmount[] | undefined,
  opts?: { allowEmpty?: boolean },
): WorkstationRunner[] {
  const list = horsesDrawLast(horses ?? [])
  if (list.length === 0) {
    // Fancy/session Detail Book often has no horse until API fills No|Yes —
    // do not invent Match Odds "1"/"2" zeros (painted blank exposure).
    if (opts?.allowEmpty) return []
    return [
      { id: '1', name: '1', amountPt: 0, amountTotal: 0, ...emptyOdds() },
      { id: '2', name: '2', amountPt: 0, amountTotal: 0, ...emptyOdds() },
    ]
  }
  return list.map((h, i) => {
    const amount = horseAmount(h)
    const id = String(h.horse || i + 1)
    return {
      id,
      name: horseName(h, i),
      amountPt: amount,
      amountTotal: amount,
      ...emptyOdds(),
    }
  })
}

function treeHorseFromRunners(runners: WorkstationRunner[]): WorkstationHorseAmount[] {
  return runners.map((r) => ({
    runnerId: r.id,
    amountPt: r.amountPt,
    amountTotal: r.amountTotal,
  }))
}

/**
 * Single-node tree from getTotalBooks1 book[0] (Lab first paint before downline expand).
 *
 * Match Odds Net Exposure = worst case = min(horse amounts), never the sum.
 * Do **not** let `getMemberNetExposure` (player-level sum for the agent login,
 * often 0) clobber that — it painted Rs.0 while horses showed +10 / −4.
 */
export function treeFromTotalBook(
  row: TotalBooks1BookRow,
  runners: WorkstationRunner[],
  memberNe?: number | null,
  marketType?: WorkstationMarketType,
  accessLevel?: string,
): WorkstationTreeNode {
  const fancy =
    marketType === 'fancy' || Boolean(row.fancyFlag) || Boolean(row.lineLadder)
  const amounts = runners.map((r) => r.amountPt)
  let minNe: number
  if (fancy) {
    minNe =
      row.netExposure != null && Number.isFinite(Number(row.netExposure))
        ? Number(row.netExposure)
        : row.totalNetExposure != null && Number.isFinite(Number(row.totalNetExposure))
          ? Number(row.totalNetExposure)
          : amounts.length > 0
            ? Math.min(...amounts)
            : memberNe != null && Number.isFinite(memberNe)
              ? memberNe
              : 0
  } else if (amounts.length > 0) {
    minNe = Math.min(...amounts)
  } else if (row.totalNetExposure != null && Number.isFinite(Number(row.totalNetExposure))) {
    minNe = Number(row.totalNetExposure)
  } else if (memberNe != null && Number.isFinite(memberNe)) {
    minNe = memberNe
  } else {
    minNe = Number(row.netExposure) || 0
  }
  const fancyNe = fancy ? minNe : Number(row.netExposure) || minNe
  return {
    id: row.user || 'self',
    username: row.username?.trim() || 'self',
    level: 'agent',
    ...(accessLevel ? { accessLevel } : {}),
    minNetExposurePt: minNe,
    minNetExposureTotal: minNe,
    netExposurePt: fancyNe,
    netExposureTotal: fancyNe,
    horse: treeHorseFromRunners(runners),
  }
}

/**
 * Lab `net-exposure-tracker` `getParticularBooks` child row → tree node.
 * Horse amounts come from the child book; columns stay the parent market runners.
 */
export function treeNodeFromDownlineBook(
  row: TotalBooks1BookRow,
  runners: WorkstationRunner[],
  marketType: WorkstationMarketType,
  meta: { username?: string; level?: 'agent' | 'player'; accessLevel?: string } = {},
): WorkstationTreeNode {
  const byHorse = new Map(
    (row.horse ?? []).map((h) => [String(h.horse), Number(h.amount) || 0]),
  )
  const childRunners = runners.map((r) => {
    const amount = byHorse.has(r.id) ? byHorse.get(r.id)! : 0
    return { ...r, amountPt: amount, amountTotal: amount }
  })
  const base = treeFromTotalBook(row, childRunners, null, marketType, meta.accessLevel)
  return {
    ...base,
    id: String(row.user || base.id),
    username: (meta.username || row.username || base.username).trim() || base.username,
    level: meta.level ?? 'agent',
    ...(meta.accessLevel ? { accessLevel: meta.accessLevel } : {}),
  }
}

/** Lab keeps children with non-empty horse books only. */
export function hasDownlineBookPosition(row: TotalBooks1BookRow | undefined | null): boolean {
  return Boolean(row?.horse && row.horse.length > 0)
}

/** Lab bet ticker → workstation bet row (matched/unmatched like workstation.js). */
export function toWorkstationBet(row: WorkstationBetRow): WorkstationBet {
  // POINTS player.shareBp is Master keep — never scale stake by player.rate.
  const ccy = String(row.currencyType || 'INR').toUpperCase()
  const points = ccy === 'POINTS' || ccy === 'POINT@1'
  const rate = points
    ? 1
    : row.player?.rate != null && Number.isFinite(row.player.rate)
      ? Number(row.player.rate)
      : 1
  const stake = (Number(row.stake) || 0) * rate
  const amountExecuted = Number(row.amountExecuted) || 0
  const amountToPlace = Number(row.amountToBePlacedOnBetfair) || stake || 1
  const pct = amountToPlace > 0 ? (amountExecuted * 100) / amountToPlace : 100
  const matchedAmt = (stake * pct) / 100
  const unMatchedAmt = Math.max(0, stake - matchedAmt)
  const betRate = Number(row.betRate) || 0
  const averagePriceMatched =
    row.averagePriceMatched && Number(row.averagePriceMatched) !== 0
      ? Number(row.averagePriceMatched)
      : betRate
  const sideRaw = String(row.type || 'BACK').toUpperCase()
  const type: WorkstationBetSide = sideRaw === 'LAY' ? 'LAY' : 'BACK'
  const profitLiability =
    type === 'BACK' ? Math.max(0, matchedAmt * (averagePriceMatched - 1)) : matchedAmt

  return {
    id: row.betId,
    userName: row.userName || '',
    currencyType: row.currencyType || 'INR',
    placedAt: row.placedDate || row.createdAt || new Date().toISOString(),
    matchedAt: row.matchedTime ?? null,
    status: row.status || '',
    event: row.event || '',
    marketName: row.marketName || '',
    selectionName: row.selectionName || '',
    type,
    betRate,
    runs: row.runs != null && Number.isFinite(Number(row.runs)) ? Number(row.runs) : null,
    averagePriceMatched,
    matchedAmt,
    unMatchedAmt,
    profitLiability,
    ip: row.IP || '',
  }
}

export function marketFromBooksGroups(
  groups: BookEventGroupBackend[] | undefined,
  marketId: string,
): BookMarketBackend | undefined {
  for (const g of groups ?? []) {
    const hit = g.data.find((m) => m.marketId === marketId)
    if (hit) return hit
  }
  return undefined
}

export type AssembleWorkstationInput = {
  marketId: string
  bookType: BookType
  total?: TotalBooks1Response | null
  booksMarket?: BookMarketBackend | null
  bets?: BetDetailsForWorkstationResponse | null
  memberNe?: number | null
  /** Route/search fallbacks when API omits names. */
  fallback?: {
    eventName?: string
    marketName?: string
    type?: WorkstationMarketType
    username?: string
    /** Lab book.level for root PT ladder (e.g. Master). */
    accessLevel?: string
  }
}

/**
 * Betfair line session only (`lineLadder` / overs–runs line).
 * Does **not** treat custom fancy as line.
 * Uses Lab raw type / `lineLadder` flag, marketId `#`+`-`, and name heuristics
 * (UI maps lineLadder → fancy layout, so type alone is not enough).
 */
export function isLineSessionMarket(input: {
  marketType?: string | null
  marketId?: string | null
  marketName?: string | null
  lineLadder?: boolean | null
}): boolean {
  if (input.lineLadder === true) return true
  const marketType = String(input.marketType || '')
    .trim()
    .toLowerCase()
  if (marketType === 'lineladder' || marketType === 'line_ladder') return true
  // Never promote Match Odds / Bookmaker via id/name heuristics.
  if (
    marketType === 'match' ||
    marketType === 'matchodds' ||
    marketType === 'bookmaker'
  ) {
    return false
  }
  const marketId = String(input.marketId || '')
  const idLower = marketId.toLowerCase()
  if (
    marketId.includes('#') &&
    marketId.includes('-') &&
    !idLower.includes('bookmaker')
  ) {
    return true
  }
  const name = String(input.marketName || '')
    .trim()
    .toLowerCase()
  if (!name || name.includes('fancy') || name.includes('bookmaker')) return false
  return (
    name.includes('line session') ||
    name.includes('runs line') ||
    name.includes('overs line') ||
    name.includes('ball line') ||
    /\bline$/.test(name)
  )
}

/**
 * Assemble a `WorkstationBook` from the four Lab workstation endpoints.
 * Returns null when there is no market book row to render.
 */
export function assembleWorkstationBook(input: AssembleWorkstationInput): WorkstationBook | null {
  const row =
    input.total?.book?.[0] ??
    (input.booksMarket
      ? ({ ...input.booksMarket, username: input.fallback?.username } as TotalBooks1BookRow)
      : undefined)
  if (!row) return null

  const type = labTypeToWorkstation(
    input.total?.type,
    row.marketName || input.fallback?.marketName,
    input.fallback?.type ?? 'match',
  )
  const lineLadder =
    input.total?.type === 'lineLadder' || Boolean(row.lineLadder)
  const runners = runnersFromHorses(row.horse, { allowEmpty: type === 'fancy' })
  const withUser: TotalBooks1BookRow = {
    ...row,
    username: row.username?.trim() || input.fallback?.username || row.username,
  }

  return {
    marketId: input.marketId,
    type,
    marketName: row.marketName || input.fallback?.marketName || input.marketId,
    eventName: row.eventName || input.fallback?.eventName || 'Market',
    status: 'OPEN',
    ...(lineLadder ? { lineLadder: true } : {}),
    runners,
    tree: treeFromTotalBook(
      withUser,
      runners,
      input.memberNe,
      type,
      input.fallback?.accessLevel,
    ),
    bets: (input.bets?.result ?? []).map(toWorkstationBet),
  }
}
